import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Wrench, AlertCircle, AlertTriangle, CheckCircle, Send, FileText, User } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function SendToMechanicDialog({ open, onOpenChange, busId, busName }) {
  const [expiredParams, setExpiredParams] = useState([]);
  const [busNotes, setBusNotes] = useState('');
  const [selectedParams, setSelectedParams] = useState([]);
  const [paramDescriptions, setParamDescriptions] = useState({}); // Описания параметров
  const [mechanics, setMechanics] = useState([]);
  const [selectedMechanic, setSelectedMechanic] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open && busId) {
      fetchData();
    }
  }, [open, busId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [paramsRes, usersRes] = await Promise.all([
        axios.get(`${API}/api/buses/${busId}/expired-params`, getAuthHeaders()),
        axios.get(`${API}/api/users`, getAuthHeaders())
      ]);

      const params = paramsRes.data.expired_params || [];
      setExpiredParams(params);
      setBusNotes(paramsRes.data.bus_notes || '');

      // Инициализируем описания из параметров
      const descriptions = {};
      params.forEach(p => {
        descriptions[p.parameter_id] = p.description || '';
      });
      setParamDescriptions(descriptions);

      const mechanicUsers = usersRes.data.filter(u => u.is_mechanic);
      setMechanics(mechanicUsers);

      // Select all by default
      setSelectedParams(params.map(p => p.parameter_id));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const toggleParam = (paramId) => {
    setSelectedParams(prev =>
      prev.includes(paramId)
        ? prev.filter(id => id !== paramId)
        : [...prev, paramId]
    );
  };

  const selectAll = () => {
    setSelectedParams(expiredParams.map(p => p.parameter_id));
  };

  const deselectAll = () => {
    setSelectedParams([]);
  };

  const handleSend = async () => {
    if (!selectedMechanic) {
      toast.error('Выберите механика');
      return;
    }
    // Разрешаем отправку без параметров (можно отправить только с заметками)

    setSending(true);
    try {
      await axios.post(`${API}/api/mechanic-tasks`, {
        bus_id: busId,
        mechanic_id: selectedMechanic,
        parameter_ids: selectedParams,
        parameter_descriptions: paramDescriptions,
        admin_note: adminNote,
        bus_notes: busNotes
      }, getAuthHeaders());

      toast.success('Работа отправлена механику');
      onOpenChange(false);
    } catch (error) {
      console.error('Error sending task:', error);
      toast.error('Ошибка отправки');
    } finally {
      setSending(false);
    }
  };

  const criticalParams = expiredParams.filter(p => p.is_critical);
  const nonCriticalParams = expiredParams.filter(p => !p.is_critical);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="send-to-mechanic-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <Wrench className="w-5 h-5 text-orange-600" />
            Отправить работу — {busName}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-4 pr-1">
            {/* Bus notes - editable */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-semibold text-amber-800">Заметки об автобусе</span>
              </div>
              <textarea
                data-testid="bus-notes-input"
                value={busNotes}
                onChange={(e) => setBusNotes(e.target.value)}
                placeholder="Заметки об автобусе (будут видны механику)"
                className="w-full bg-white border border-amber-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-500"
                rows={2}
              />
            </div>

            {/* No expired params */}
            {expiredParams.length === 0 ? (
              <div className="text-center py-6 bg-green-50 rounded-lg border border-green-200">
                <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-2" />
                <p className="text-green-700 font-medium">Все параметры в норме</p>
                <p className="text-sm text-gray-500 mt-1">Можно отправить задание с заметками</p>
              </div>
            ) : (
              <>
                {/* Select controls */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 font-medium">
                    Выбрано: {selectedParams.length} из {expiredParams.length}
                  </span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={selectAll}>Выбрать все</Button>
                    <Button variant="outline" size="sm" onClick={deselectAll}>Снять все</Button>
                  </div>
                </div>

                {/* Critical params */}
                {criticalParams.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-4 h-4 text-red-500" />
                      <span className="text-sm font-bold text-red-700">Критические ({criticalParams.length})</span>
                    </div>
                    <div className="space-y-2">
                      {criticalParams.map(param => (
                        <div
                          key={param.parameter_id}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            selectedParams.includes(param.parameter_id)
                              ? 'border-red-400 bg-red-50'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                          data-testid={`param-${param.parameter_id}`}
                        >
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedParams.includes(param.parameter_id)}
                              onChange={() => toggleParam(param.parameter_id)}
                              className="w-4 h-4 mt-0.5 text-red-600 rounded"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900">{param.parameter_name}</span>
                                <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-medium">
                                  {param.status_text}
                                </span>
                              </div>
                            </div>
                          </label>
                          <input
                            type="text"
                            value={paramDescriptions[param.parameter_id] || ''}
                            onChange={(e) => setParamDescriptions(prev => ({
                              ...prev,
                              [param.parameter_id]: e.target.value
                            }))}
                            placeholder="Описание работы..."
                            className="w-full mt-2 text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Non-critical params */}
                {nonCriticalParams.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-bold text-gray-600">Некритические ({nonCriticalParams.length})</span>
                    </div>
                    <div className="space-y-2">
                      {nonCriticalParams.map(param => (
                        <div
                          key={param.parameter_id}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            selectedParams.includes(param.parameter_id)
                              ? 'border-orange-300 bg-orange-50'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                          data-testid={`param-${param.parameter_id}`}
                        >
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedParams.includes(param.parameter_id)}
                              onChange={() => toggleParam(param.parameter_id)}
                              className="w-4 h-4 mt-0.5 text-orange-600 rounded"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900">{param.parameter_name}</span>
                                <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full font-medium">
                                  {param.status_text}
                                </span>
                              </div>
                            </div>
                          </label>
                          <input
                            type="text"
                            value={paramDescriptions[param.parameter_id] || ''}
                            onChange={(e) => setParamDescriptions(prev => ({
                              ...prev,
                              [param.parameter_id]: e.target.value
                            }))}
                            placeholder="Описание работы..."
                            className="w-full mt-2 text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Select mechanic - always visible */}
            <div className="pt-2 border-t">
              <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                <User className="w-4 h-4 inline mr-1" />
                Назначить механику
              </Label>
              {mechanics.length === 0 ? (
                <p className="text-sm text-red-500">Нет доступных механиков. Создайте пользователя с ролью механика.</p>
              ) : (
                <Select value={selectedMechanic} onValueChange={setSelectedMechanic}>
                  <SelectTrigger data-testid="select-mechanic">
                    <SelectValue placeholder="Выберите механика" />
                  </SelectTrigger>
                  <SelectContent>
                    {mechanics.map(m => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.first_name || m.last_name
                          ? `${m.first_name || ''} ${m.last_name || ''}`.trim()
                          : m.username}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Admin note - always visible */}
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                <FileText className="w-4 h-4 inline mr-1" />
                Важная работа
              </Label>
              <textarea
                data-testid="admin-note-input"
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder="Уточнения по работе (необязательно)"
                className="w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-500"
                rows={3}
              />
            </div>
          </div>
        )}

        {/* Actions */}
        {!loading && (
          <div className="flex gap-3 pt-4 border-t mt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Отмена
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending || !selectedMechanic}
              className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold"
              data-testid="send-task-button"
            >
              {sending ? 'Отправка...' : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Отправить {selectedParams.length > 0 ? `(${selectedParams.length})` : ''}
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
